import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { type ApolloError } from '@apollo/react-hooks';
import type { PreliminaryCheck, SuggestOffer } from './types';
import { PAGE_TEXT } from '../../../components/lang';
import { PROGRAMME_NAME } from '../../../../Application/components';
import { getOptionsLabel } from '../../../../Monitor/components';
import type { SelectOption } from '../../../../../components';
import { OfferSuggestion } from '../../../constants';
import { getProgrammeType } from '../../../components';
import { OFFER_TYPE } from '../../../../Assessment';

type GetPreCheck = {
  preliminaryCheck: PreliminaryCheck;
  suggestOffers: SuggestOffer[];
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

export const useGetPreCheck = (applicationId: string): GetPreCheck => {
  const { data, loading, error, refetch } = useQuery(
    gql`
      query ($applicationId: ID!) {
        preliminaryCheck(applicationId: $applicationId) {
          rules {
            programmeLevel
            duration
            version
            checkRule {
              item
              result
            }
            result
          }
        }
      }
    `,
    {
      variables: { applicationId },
      skip: !applicationId,
      fetchPolicy: 'network-only',
    },
  );

  const capitalizeFirstLetter = (str: string): string => {
    return str.charAt(0)?.toUpperCase() + str.slice(1).toLowerCase();
  };

  let preliminaryCheck: PreliminaryCheck = {
    rules: [],
  };

  const programmeConvert: SelectOption[] = [
    {
      value: PROGRAMME_NAME.MASTER_OF_PHILOSOPHY,
      label: PAGE_TEXT.GSB1_FORM.MPHIL,
    },
    {
      value: PROGRAMME_NAME.DOCTOR_OF_PHILOSOPHY,
      label: PAGE_TEXT.GSB1_FORM.PHD,
    },
  ];
  if (!loading) {
    preliminaryCheck = {
      rules:
        (data?.preliminaryCheck as PreliminaryCheck)?.rules.map(rule => ({
          ...rule,
          programmeLevel: getOptionsLabel(
            programmeConvert,
            rule.programmeLevel as PROGRAMME_NAME,
          ),
          // only display the item that related to ENGLISH or REQUIREMENT
          checkRule: rule.checkRule
            .filter(
              check =>
                check.item.includes('ENGLISH') ||
                check.item.includes('REQUIREMENT'),
            )
            .map(check => ({
              item: check.item.includes('ENGLISH')
                ? PAGE_TEXT.GSB1_FORM.ENGLISH_LANGUAGE
                : PAGE_TEXT.GSB1_FORM.GENERAL_ENTRANCE,
              result: capitalizeFirstLetter(check.result).replace('_', ' '),
            })),
          result: capitalizeFirstLetter(rule.result).replace('_', ' '),
        })) ?? [],
    };
  }

  const suggestOffers: SuggestOffer[] = preliminaryCheck.rules
    .filter(
      rule =>
        rule.result !== OfferSuggestion.REJECT &&
        rule.result !== OfferSuggestion.NOT_RECOMMENDED,
    )
    .flatMap(rule =>
      getProgrammeType(rule.programmeLevel, rule.duration).map(programme => ({
        programme,
        offerType: rule.result.includes(OfferSuggestion.FIRM)
          ? OFFER_TYPE.FIRM
          : OFFER_TYPE.CONDITION,
      })),
    );

  return { preliminaryCheck, suggestOffers, loading, error, refetch };
};
